import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  pharmaciesData, accreditedStructuresData, medicalDeviceKitsData,
  getDeviceModelById, deviceUsageData, pharmacyMonthlyPerformance, getKitById
} from "@/data/platformData";
import {
  deviceInstancesData, getDevicesByPharmacy, timeAgo, healthStatusStyle,
  centralIntegrations, regionalIntegrations, integrationStatusLabel, integrationStatusStyle
} from "@/data/adminInfraData";
import { structureBillingData } from "@/data/platformData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  BarChart3, Cpu, Building2, Hospital, Plug, Wifi, WifiOff, Activity, AlertTriangle,
  Globe2, MapPin, Euro, RefreshCw, CheckCircle2, AlertOctagon
} from "lucide-react";

export const AdminMonitoringPage = () => {
  // Devices stats
  const totalDevices = deviceInstancesData.length;
  const onlineDevices = deviceInstancesData.filter(d => d.online).length;
  const errorDevices = deviceInstancesData.filter(d => d.healthCheck.status === "error").length;
  const warningDevices = deviceInstancesData.filter(d => d.healthCheck.status === "warning").length;

  // Per-pharmacy device count
  const perPharmacyDevices = pharmaciesData.map(ph => {
    const devs = getDevicesByPharmacy(ph.id);
    return {
      name: ph.name.replace("Farmacia ", "").slice(0, 18),
      online: devs.filter(d => d.online).length,
      offline: devs.filter(d => !d.online).length
    };
  });

  // Per-pharmacy revenue (last 6 months)
  const phRevenue = pharmaciesData.map(ph => {
    const monthly = pharmacyMonthlyPerformance.filter(m => m.pharmacyId === ph.id);
    const total = monthly.reduce((acc, m) => acc + m.fatturato, 0);
    const procs = monthly.reduce((acc, m) => acc + m.prestazioni, 0);
    return { id: ph.id, name: ph.name, total, procs };
  });

  // Per-structure billing
  const strRevenue = accreditedStructuresData.map(s => {
    const rows = structureBillingData.filter(r => r.structureId === s.id);
    const total = rows.reduce((a, b) => a + b.total, 0);
    const count = rows.reduce((a, b) => a + b.count, 0);
    return { id: s.id, name: s.name, total, count };
  });

  // Integrations summary
  const allInt = [...centralIntegrations, ...regionalIntegrations];
  const intOK = allInt.filter(i => i.status === "operational").length;
  const intDeg = allInt.filter(i => i.status === "degraded").length;
  const intDown = allInt.filter(i => i.status === "down").length;

  const refreshInt = (id) => toast.success("Sincronizzazione richiesta", { description: `Polling integrazione ${id}...` });

  // Group regional integrations by region
  const regionalGrouped = useMemo(() => {
    const m = {};
    regionalIntegrations.forEach(i => { if (!m[i.region]) m[i.region] = []; m[i.region].push(i); });
    return m;
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="h-4 w-4 text-slate-600" />
          <span className="text-xs uppercase tracking-wider text-muted-foreground">5 · Monitoraggio & Rendicontazione</span>
          <MDRBadge variant="admin" size="sm" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Centro di controllo piattaforma</h1>
        <p className="text-sm text-muted-foreground mt-1">Stato dispositivi, ricavi, integrazioni nazionali e regionali</p>
      </div>

      <Tabs defaultValue="devices" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="devices" data-testid="mon-tab-devices">Device singoli</TabsTrigger>
          <TabsTrigger value="kits" data-testid="mon-tab-kits">Kit & modelli</TabsTrigger>
          <TabsTrigger value="pharmacies" data-testid="mon-tab-pharmacies">Farmacie</TabsTrigger>
          <TabsTrigger value="structures" data-testid="mon-tab-structures">Strutture</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="mon-tab-integrations">Integrazioni</TabsTrigger>
        </TabsList>

        {/* DEVICES — singoli */}
        <TabsContent value="devices" className="space-y-4">
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[
              { label: "Totale device", value: totalDevices, icon: Cpu, accent: "text-primary bg-primary/10" },
              { label: "Online", value: onlineDevices, icon: Wifi, accent: "text-emerald-700 bg-emerald-100" },
              { label: "Warning health", value: warningDevices, icon: AlertTriangle, accent: "text-amber-700 bg-amber-100" },
              { label: "Errori", value: errorDevices, icon: AlertOctagon, accent: "text-rose-700 bg-rose-100" }
            ].map(k => {
              const I = k.icon;
              return (
                <Card key={k.label}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.accent}`}><I className="h-5 w-5" /></div>
                    <div><p className="text-2xl font-bold leading-none">{k.value}</p><p className="text-xs mt-1 text-muted-foreground">{k.label}</p></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Distribuzione online/offline per farmacia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={perPharmacyDevices}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="online" stackId="s" fill="#10b981" name="Online" />
                    <Bar dataKey="offline" stackId="s" fill="#f43f5e" name="Offline" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Elenco device singoli</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {deviceInstancesData.map(d => {
                const m = getDeviceModelById(d.deviceModelId);
                const ph = pharmaciesData.find(p => p.id === d.pharmacyId);
                return (
                  <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md border bg-card text-sm" data-testid={`mon-device-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{m?.name} <span className="text-muted-foreground font-mono text-xs">· {d.serialNumber}</span></p>
                      <p className="text-xs text-muted-foreground">{ph?.name} · {timeAgo(d.lastSeen)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {d.healthCheck.supportsHealthCheck && (
                        <Badge variant="outline" className={healthStatusStyle[d.healthCheck.status]}>{d.healthCheck.status === "ok" ? "OK" : d.healthCheck.status}</Badge>
                      )}
                      <Badge variant="outline" className={d.online ? "bg-emerald-50 text-emerald-700 border-emerald-200 gap-1" : "bg-rose-50 text-rose-700 border-rose-200 gap-1"}>
                        {d.online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                        {d.online ? "Online" : "Offline"}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* KITS — modelli */}
        <TabsContent value="kits" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Utilizzo per modello (totale piattaforma)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {deviceUsageData.map(u => {
                  const m = getDeviceModelById(u.deviceModelId);
                  if (!m) return null;
                  return (
                    <div key={u.deviceModelId} className="flex items-center justify-between p-3 rounded-md border bg-card text-sm">
                      <div>
                        <p className="font-medium">{m.name}</p>
                        <p className="text-xs text-muted-foreground">{m.type} · ultimo uso {u.lastUse}</p>
                      </div>
                      <div className="flex gap-3 text-right">
                        <div><p className="text-xs text-muted-foreground">Esami</p><p className="font-semibold">{u.uses}</p></div>
                        <Badge variant="outline" className={u.uptime >= 99 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{u.uptime}% uptime</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Composizione kit attivi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2">
                {medicalDeviceKitsData.map(k => (
                  <div key={k.id} className="p-3 rounded-md border">
                    <p className="font-medium text-sm">{k.name}</p>
                    <p className="text-xs text-muted-foreground">{k.deviceModelIds.length} elettromedicali</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PHARMACIES — rendicontazione */}
        <TabsContent value="pharmacies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Euro className="h-4 w-4 text-emerald-600" /> Fatturato per farmacia (ultimi 6 mesi)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {phRevenue.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-md border bg-card" data-testid={`mon-pharmacy-${r.id}`}>
                    <div>
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.procs} prestazioni</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-700">€ {r.total.toLocaleString("it-IT")}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STRUCTURES — rendicontazione */}
        <TabsContent value="structures" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Hospital className="h-4 w-4 text-cyan-600" /> Fatturato per struttura accreditata</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {strRevenue.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 rounded-md border bg-card" data-testid={`mon-structure-${r.id}`}>
                    <div>
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.count} refertazioni</p>
                    </div>
                    <p className="text-xl font-bold text-cyan-700">€ {r.total.toLocaleString("it-IT")}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS — central + regional */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="grid gap-4 grid-cols-3">
            {[
              { label: "Operative", value: intOK, icon: CheckCircle2, accent: "text-emerald-700 bg-emerald-100" },
              { label: "Degradate", value: intDeg, icon: AlertTriangle, accent: "text-amber-700 bg-amber-100" },
              { label: "Non disponibili", value: intDown, icon: AlertOctagon, accent: "text-rose-700 bg-rose-100" }
            ].map(k => {
              const I = k.icon;
              return (
                <Card key={k.label}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${k.accent}`}><I className="h-5 w-5" /></div>
                    <div><p className="text-2xl font-bold leading-none">{k.value}</p><p className="text-xs mt-1 text-muted-foreground">{k.label}</p></div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Globe2 className="h-4 w-4 text-indigo-600" /> Integrazioni nazionali</CardTitle>
              <p className="text-xs text-muted-foreground">Servizi centrali di livello sistema sanitario nazionale</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {centralIntegrations.map(i => (
                <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md border bg-card" data-testid={`integration-${i.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.description}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Sync {i.syncFrequency} · Ultimo: {timeAgo(i.lastSync)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={integrationStatusStyle[i.status]}>{integrationStatusLabel[i.status]}</Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refreshInt(i.id)} title="Forza sync"><RefreshCw className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {Object.entries(regionalGrouped).map(([region, list]) => (
            <Card key={region}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4 text-cyan-600" /> {region}</CardTitle>
                <p className="text-xs text-muted-foreground">Anagrafe regionale, FSE regionale, Albo medici</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {list.map(i => (
                  <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md border bg-card" data-testid={`integration-${i.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{i.name}</p>
                      <p className="text-xs text-muted-foreground">{i.description}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">Sync {i.syncFrequency} · Ultimo: {timeAgo(i.lastSync)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={integrationStatusStyle[i.status]}>{integrationStatusLabel[i.status]}</Badge>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => refreshInt(i.id)}><RefreshCw className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminMonitoringPage;
